import React from "react";
import { useTranslation } from "react-i18next";

const variants = {
  primary:
    "bg-zinc-950 text-white hover:bg-zinc-800 focus:ring-zinc-300 border border-zinc-950",
  warning:
    "bg-red-600 text-white hover:bg-red-700 focus:ring-red-300 border border-red-600",
  secondary:
    "bg-white text-zinc-900 hover:bg-zinc-50 focus:ring-zinc-200 border border-zinc-200",
  dark:
    "bg-zinc-900 text-white hover:bg-zinc-800 focus:ring-zinc-300 border border-zinc-900",
  ghost:
    "bg-transparent text-zinc-700 hover:bg-zinc-100 focus:ring-zinc-200 border border-transparent",
  // Elite look: white base with gold accent for master-control actions.
  elite:
    "bg-white text-[#8a6a1c] hover:bg-[#fbf6e7] focus:ring-amber-300 border-2 border-[#c9a24a] shadow-sm font-semibold",
  eliteFilled:
    "bg-[#c9a24a] text-white hover:bg-[#b48a2b] focus:ring-amber-300 border-2 border-[#b48a2b] shadow-sm font-semibold",
};

const sizes = {
  sm: "h-8 px-3 text-xs",
  md: "h-10 px-4 text-sm",
  lg: "h-11 px-5 text-sm",
  xl: "h-12 px-6 text-base",
};

export const BrutalButton = React.forwardRef(function Button(
  { variant = "primary", size = "md", className = "", children, ...props },
  ref
) {
  return (
    <button
      ref={ref}
      className={`btn-brutal ${variants[variant]} ${sizes[size]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
});

export function BrutalCard({ className = "", children, ...rest }) {
  return (
    <div className={`card-brutal p-5 sm:p-6 ${className}`} {...rest}>
      {children}
    </div>
  );
}

export function BrutalInput({ className = "", ...props }) {
  return <input className={`input-brutal ${className}`} {...props} />;
}

export function BrutalTextarea({ className = "", ...props }) {
  return (
    <textarea
      className={`w-full bg-white border border-zinc-200 rounded-lg px-3.5 py-2.5 text-sm font-normal text-zinc-950 placeholder:text-zinc-400 outline-none transition-all focus:border-[#c9a24a] focus:ring-3 focus:ring-[#c9a24a]/15 ${className}`}
      {...props}
    />
  );
}

export function StockBadge({ percent }) {
  const { t } = useTranslation();
  let label = t("stock.healthy");
  let cls = "bg-emerald-50 text-emerald-700 border border-emerald-200";
  if (percent < 40) {
    label = t("stock.low");
    cls = "bg-red-50 text-red-700 border border-red-200";
  } else if (percent < 60) {
    label = t("stock.watch");
    cls = "bg-amber-50 text-amber-700 border border-amber-200";
  }
  return (
    <span
      data-testid="stock-badge"
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 ${cls} text-xs font-medium rounded-full whitespace-nowrap`}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      {label} · {Math.round(percent)}%
    </span>
  );
}

export function Section({ title, subtitle, action, children, testId }) {
  return (
    <section data-testid={testId} className="space-y-4">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h2 className="text-lg sm:text-xl font-semibold text-zinc-950 tracking-tight">{title}</h2>
          {subtitle && <p className="text-sm text-zinc-500 mt-0.5">{subtitle}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}
